import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneRight, Users, Envelope, ChatCircleText, ArrowLeft, XCircle, UploadSimple, WhatsappLogo } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";

// Two-way email center — bulk campaigns + per-contact conversation threads.
// Data-testid coverage on every actionable element per repo convention.

export default function EmailCenter() {
  const [tab, setTab] = useState("compose"); // compose | campaigns | thread
  const [mode, setMode] = useState("contacts"); // "contacts" | "leads" | "adhoc"
  const [adHocEmails, setAdHocEmails] = useState(""); // pasted addresses
  const [importing, setImporting] = useState(false);
  const csvRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("<p>Hi {{name}},</p>\n<p>...</p>\n<p>— Team Shelvestech</p>");
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [detail, setDetail] = useState(null); // {campaign, recipients}
  const [threadContactId, setThreadContactId] = useState("");
  const [thread, setThread] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [c, camps, l] = await Promise.all([
        api.get("/contacts"),
        api.get("/email/campaigns"),
        api.get("/leads").catch(() => ({ data: [] })),
      ]);
      setContacts(c.data || []);
      setCampaigns(camps.data || []);
      setLeads(l.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const filteredContacts = contacts.filter((c) => {
    if (!c.email) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.company || "").toLowerCase().includes(s);
  });

  const filteredLeads = leads.filter((l) => {
    if (!l.email) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(s)
      || (l.email || "").toLowerCase().includes(s)
      || (l.company || "").toLowerCase().includes(s)
      || (l.status || "").toLowerCase().includes(s);
  });

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filteredContacts.length) setSelected(new Set());
    else setSelected(new Set(filteredContacts.map((c) => c.id)));
  };
  const toggleLead = (id) => {
    const next = new Set(selectedLeads);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedLeads(next);
  };
  const toggleAllLeads = () => {
    if (selectedLeads.size === filteredLeads.length) setSelectedLeads(new Set());
    else setSelectedLeads(new Set(filteredLeads.map((l) => l.id)));
  };

  const sendCampaign = async () => {
    if (!subject.trim() || !bodyHtml.trim()) { toast.error("Subject and body required"); return; }
    if (mode === "contacts" && selected.size === 0) { toast.error("Pick at least one contact"); return; }
    if (mode === "leads" && selectedLeads.size === 0) { toast.error("Pick at least one lead"); return; }
    if (mode === "adhoc" && !adHocEmails.trim()) { toast.error("Paste at least one email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/email/campaigns", {
        name: name || `Campaign ${new Date().toISOString().slice(0, 10)}`,
        subject, body_html: bodyHtml,
        contact_ids: mode === "contacts" ? Array.from(selected) : [],
        lead_ids: mode === "leads" ? Array.from(selectedLeads) : [],
        ad_hoc_emails: mode === "adhoc" ? [adHocEmails] : [],
      });
      toast.success(`Campaign sent · ${data.sent} delivered · ${data.failed} failed (${data.total} total)`);
      setSelected(new Set()); setSelectedLeads(new Set()); setAdHocEmails(""); setName(""); setSubject(""); setBodyHtml("<p>Hi {{name}},</p>\n<p>...</p>");
      await load();
      setTab("campaigns");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const importContactsCSV = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/email/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} contact(s) · ${data.skipped} skipped (duplicates or missing email)`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const openCampaign = async (id) => {
    try {
      const { data } = await api.get(`/email/campaigns/${id}`);
      setDetail(data);
    } catch (e) { toast.error("Load failed"); }
  };

  const openThread = async (contactId) => {
    setThreadContactId(contactId);
    setTab("thread");
    try {
      const { data } = await api.get(`/email/thread/${contactId}`);
      setThread(data);
      setReplySubject(data.messages?.length ? `Re: ${data.messages[data.messages.length - 1].subject.replace(/^Re:\s*/i, "")}` : "");
    } catch (e) { toast.error("Load thread failed"); }
  };

  const sendReply = async () => {
    if (!replySubject.trim() || !replyBody.trim()) { toast.error("Subject and body required"); return; }
    setReplying(true);
    try {
      await api.post("/email/reply", { contact_id: threadContactId, subject: replySubject, body_html: replyBody });
      toast.success("Reply sent");
      setReplyBody("");
      await openThread(threadContactId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setReplying(false); }
  };

  const sendWhatsAppNudge = async () => {
    if (!thread?.contact?.phone) { toast.error("Contact has no phone number — add one on the contact record"); return; }
    const template = window.prompt(
      `Send WhatsApp template to ${thread.contact.name || thread.contact.email} (${thread.contact.phone})?\n\nEnter the pre-approved template name (default: shelves_info):`,
      "shelves_info"
    );
    if (!template) return;
    try {
      const { data } = await api.post("/whatsapp/send", {
        phone: thread.contact.phone, contact_id: threadContactId, template_name: template,
      });
      toast.success(`WhatsApp '${template}' sent · msg id ${data.external_message_id || "OK"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp send failed");
    }
  };

  return (
    <Layout title="Email Center" subtitle="Bulk campaigns · Inbound replies · Two-way threading">
      <div className="flex gap-1 border-b border-black/10 mb-6 flex-wrap">
        {[
          { k: "compose", l: "✉ Compose Campaign", tid: "tab-compose" },
          { k: "campaigns", l: `📊 Campaigns (${campaigns.length})`, tid: "tab-campaigns" },
          { k: "thread", l: "💬 Conversation", tid: "tab-thread" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={t.tid}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="compose-section">
          {/* Contact list */}
          <div className="lg:col-span-5 border border-black/10 bg-white flex flex-col" style={{ maxHeight: "72vh" }}>
            <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2"><Users size={16}/><span className="font-display font-bold">Recipients</span></div>
              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={() => setMode("contacts")} data-testid="mode-contacts-btn" className={`px-2 py-1 text-[10px] font-mono-shelv uppercase tracking-widest ${mode === "contacts" ? "bg-[#D90429] text-white" : "border border-black/15 text-[#717680]"}`}>Contacts ({contacts.filter(c => c.email).length})</button>
                <button onClick={() => setMode("leads")} data-testid="mode-leads-btn" className={`px-2 py-1 text-[10px] font-mono-shelv uppercase tracking-widest ${mode === "leads" ? "bg-[#D90429] text-white" : "border border-black/15 text-[#717680]"}`}>Leads ({leads.filter(l => l.email).length})</button>
                <button onClick={() => setMode("adhoc")} data-testid="mode-adhoc-btn" className={`px-2 py-1 text-[10px] font-mono-shelv uppercase tracking-widest ${mode === "adhoc" ? "bg-[#D90429] text-white" : "border border-black/15 text-[#717680]"}`}>Paste</button>
              </div>
            </div>

            {mode === "contacts" ? (
              <>
                <div className="px-3 py-2 border-b border-black/5 flex items-center gap-2">
                  <input placeholder="Search name / email / company…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contact-search" className="flex-1 border border-black/15 rounded-none px-3 py-2 text-sm"/>
                  <input ref={csvRef} type="file" accept=".csv,.xlsx,.xls" onChange={importContactsCSV} className="hidden" data-testid="csv-input"/>
                  <button onClick={() => csvRef.current?.click()} disabled={importing} data-testid="csv-import-btn" title="Bulk import contacts from CSV or Excel" className="inline-flex items-center gap-1 px-2 py-2 border border-black/15 text-xs font-semibold hover:bg-black hover:text-white disabled:opacity-40">
                    <UploadSimple size={12}/> {importing ? "…" : "CSV"}
                  </button>
                  <button onClick={toggleAll} data-testid="select-all-btn" className="text-xs font-semibold text-[#D90429] hover:underline whitespace-nowrap">
                    {selected.size === filteredContacts.length && filteredContacts.length > 0 ? "Clear" : "All"}
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredContacts.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#717680]">
                      <div className="mb-2">No contacts with an email yet.</div>
                      <div className="text-xs">Click <b>CSV</b> above to bulk import, or switch to <button onClick={() => setMode("adhoc")} className="text-[#D90429] font-semibold underline">Paste emails</button> mode.</div>
                    </div>
                  ) : filteredContacts.map((c) => (
                    <label key={c.id} className={`flex items-start gap-2 px-4 py-2 border-b border-black/5 cursor-pointer text-sm ${selected.has(c.id) ? "bg-[#FFE5E9]/50" : "hover:bg-[#FAFBFC]"}`} data-testid={`contact-row-${c.id}`}>
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="mt-1"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{c.name || "—"}</div>
                        <div className="text-xs text-[#717680] truncate">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
                      </div>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); openThread(c.id); }} data-testid={`open-thread-${c.id}`} title="View conversation" className="text-[#717680] hover:text-[#D90429]"><ChatCircleText size={16}/></button>
                    </label>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-black/10 bg-[#FAFBFC] text-xs text-[#717680] font-mono-shelv uppercase tracking-widest">{selected.size} selected</div>
              </>
            ) : mode === "leads" ? (
              <>
                <div className="px-3 py-2 border-b border-black/5 flex items-center gap-2">
                  <input placeholder="Search name / email / company / status…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" className="flex-1 border border-black/15 rounded-none px-3 py-2 text-sm"/>
                  <button onClick={toggleAllLeads} data-testid="select-all-leads-btn" className="text-xs font-semibold text-[#D90429] hover:underline whitespace-nowrap">
                    {selectedLeads.size === filteredLeads.length && filteredLeads.length > 0 ? "Clear" : "All"}
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredLeads.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#717680]">
                      <div className="mb-2">No leads with an email yet.</div>
                      <div className="text-xs">Add leads under <b>Leads</b> in the sidebar, or use <b>AI Lead Hunt</b> to auto-populate.</div>
                    </div>
                  ) : filteredLeads.map((l) => (
                    <label key={l.id} className={`flex items-start gap-2 px-4 py-2 border-b border-black/5 cursor-pointer text-sm ${selectedLeads.has(l.id) ? "bg-[#FFE5E9]/50" : "hover:bg-[#FAFBFC]"}`} data-testid={`lead-row-${l.id}`}>
                      <input type="checkbox" checked={selectedLeads.has(l.id)} onChange={() => toggleLead(l.id)} className="mt-1"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                          {l.name || "—"}
                          {l.status && <span className="text-[9px] font-mono-shelv uppercase tracking-widest px-1 py-0.5 border border-black/15 text-[#717680]">{l.status}</span>}
                        </div>
                        <div className="text-xs text-[#717680] truncate">{l.email}{l.company ? ` · ${l.company}` : ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-black/10 bg-[#FAFBFC] text-xs text-[#717680] font-mono-shelv uppercase tracking-widest">{selectedLeads.size} selected</div>
              </>
            ) : (
              <div className="p-4 flex flex-col flex-1" data-testid="adhoc-panel">
                <div className="overline text-[10px] mb-1">Paste addresses — separated by <b>;</b> <b>,</b> or newline</div>
                <textarea
                  value={adHocEmails}
                  onChange={(e) => setAdHocEmails(e.target.value)}
                  data-testid="adhoc-emails-input"
                  placeholder={"mukesh.panchal@shelvestech.com;\ninfo@shelvestech.com;\nsomeone@example.com"}
                  rows={14}
                  className="w-full flex-1 border border-black/15 rounded-none px-3 py-2 text-sm font-mono-shelv"
                />
                <div className="text-xs text-[#717680] mt-2">
                  {(adHocEmails.match(/[\w.\-+]+@[\w.\-]+\.[\w]+/g) || []).length} valid address(es) detected · duplicates and invalid formats are dropped automatically.
                </div>
                <div className="text-[11px] text-[#4A4D57] mt-2 p-2 bg-[#FAFBFC] border border-black/5">
                  <b>Tip:</b> for repeat campaigns save these as contacts — switch to <b>From contacts</b> mode and click <b>CSV</b> to upload a spreadsheet with columns <code>name, email, company</code>.
                </div>
              </div>
            )}
          </div>
          {/* Compose form */}
          <div className="lg:col-span-7 border border-black/10 bg-white p-5" data-testid="compose-form">
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <span className="overline text-[10px]">Campaign name (internal)</span>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name-input" placeholder="e.g. Feb product launch" className="w-full border border-black/15 rounded-none px-3 py-2 mt-1 text-sm"/>
              </label>
              <label className="block">
                <span className="overline text-[10px]">Subject · <b className="text-[#D90429]">supports {"{{name}}"} {"{{company}}"}</b></span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject-input" placeholder="Hi {{name}}, quick intro from Shelvestech" className="w-full border border-black/15 rounded-none px-3 py-2 mt-1 text-sm"/>
              </label>
              <label className="block">
                <span className="overline text-[10px]">Body (HTML) · use {"{{name}}"} / {"{{company}}"} placeholders</span>
                <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} data-testid="campaign-body-input" rows={12} className="w-full border border-black/15 rounded-none px-3 py-2 mt-1 text-sm font-mono-shelv"/>
              </label>
              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-[#717680]">Sends via SendGrid from <b>sales@shelvestech.com</b> · chunks of 100 with a 2s gap · delivery + open + click tracked automatically.</div>
                <button onClick={sendCampaign} disabled={sending || (mode === "contacts" ? selected.size === 0 : mode === "leads" ? selectedLeads.size === 0 : !adHocEmails.trim())} data-testid="send-campaign-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40">
                  <PaperPlaneRight size={14} weight="bold"/> {sending
                    ? "Sending…"
                    : mode === "contacts"
                      ? `Send to ${selected.size} contact${selected.size !== 1 ? "s" : ""}`
                      : mode === "leads"
                        ? `Send to ${selectedLeads.size} lead${selectedLeads.size !== 1 ? "s" : ""}`
                        : `Send to ${(adHocEmails.match(/[\w.\-+]+@[\w.\-]+\.[\w]+/g) || []).length} email(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="border border-black/10 bg-white" data-testid="campaigns-list">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
              <tr><th className="px-4 py-2">Campaign</th><th className="px-4 py-2 text-right">Recipients</th><th className="px-4 py-2 text-right">Sent</th><th className="px-4 py-2 text-right">Failed</th><th className="px-4 py-2 text-right">Opens</th><th className="px-4 py-2 text-right">Clicks</th><th className="px-4 py-2">Sent At</th></tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#717680]">No campaigns yet.</td></tr>
              ) : campaigns.map((c) => (
                <tr key={c.id} className="border-t border-black/5 hover:bg-[#FAFBFC] cursor-pointer" onClick={() => openCampaign(c.id)} data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-2 font-semibold">{c.name}<div className="text-[10px] text-[#717680]">{c.subject}</div></td>
                  <td className="px-4 py-2 text-right font-mono-shelv">{c.recipient_count}</td>
                  <td className="px-4 py-2 text-right font-mono-shelv text-[#0D9488]">{c.sent_count}</td>
                  <td className="px-4 py-2 text-right font-mono-shelv text-[#D90429]">{c.failed_count}</td>
                  <td className="px-4 py-2 text-right font-mono-shelv">{c.opens || 0}</td>
                  <td className="px-4 py-2 text-right font-mono-shelv">{c.clicks || 0}</td>
                  <td className="px-4 py-2 text-xs text-[#717680]">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail && (
            <div className="border-t border-black/10 p-4" data-testid="campaign-detail">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold">{detail.campaign.name}</div>
                <button onClick={() => setDetail(null)} data-testid="close-detail" className="text-xs text-[#717680] hover:text-black"><XCircle size={14} className="inline"/> Close</button>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
                  <tr><th className="px-3 py-1.5">Contact</th><th className="px-3 py-1.5">Email</th><th className="px-3 py-1.5">Status</th><th className="px-3 py-1.5 text-right">Opens</th><th className="px-3 py-1.5 text-right">Clicks</th><th className="px-3 py-1.5">Error</th></tr>
                </thead>
                <tbody>
                  {detail.recipients.map((r) => (
                    <tr key={r.id} className="border-t border-black/5">
                      <td className="px-3 py-1.5">{r.contact_name}</td>
                      <td className="px-3 py-1.5 text-[#717680]">{r.contact_email}</td>
                      <td className="px-3 py-1.5"><span className={`text-[10px] font-mono-shelv uppercase px-1.5 py-0.5 ${r.status === "delivered" || r.status === "sent" ? "bg-[#0D9488]/10 text-[#0D9488]" : r.status === "bounced" || r.status === "failed" ? "bg-[#D90429]/10 text-[#D90429]" : "bg-[#F4A261]/10 text-[#F4A261]"}`}>{r.status}</span></td>
                      <td className="px-3 py-1.5 text-right font-mono-shelv">{r.opens || 0}</td>
                      <td className="px-3 py-1.5 text-right font-mono-shelv">{r.clicks || 0}</td>
                      <td className="px-3 py-1.5 text-[10px] text-[#D90429]">{r.error || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "thread" && (
        <div className="border border-black/10 bg-white" data-testid="thread-panel">
          {!thread ? (
            <div className="p-8 text-center text-sm text-[#717680]">Pick a contact from the Compose tab (click the chat icon) to open their conversation.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-black/10 flex items-center gap-3">
                <button onClick={() => setTab("compose")} data-testid="back-to-compose" className="text-[#717680] hover:text-black"><ArrowLeft size={16}/></button>
                <div className="flex-1">
                  <div className="font-display font-bold">{thread.contact.name || "—"}</div>
                  <div className="text-xs text-[#717680] flex items-center gap-2">
                    <span>{thread.contact.email}</span>
                    {thread.contact.phone && <span>· <b className="text-[#25D366]">{thread.contact.phone}</b></span>}
                  </div>
                </div>
                <button
                  onClick={sendWhatsAppNudge}
                  disabled={!thread.contact.phone}
                  title={thread.contact.phone ? `Send a WhatsApp template to ${thread.contact.phone}` : "Add a phone number to this contact to enable WhatsApp"}
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <WhatsappLogo size={13} weight="fill"/> Send WhatsApp
                </button>
                <span className="text-xs font-mono-shelv text-[#717680] uppercase tracking-widest">{thread.messages.length} message{thread.messages.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="max-h-[54vh] overflow-y-auto p-4 space-y-3">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`border-l-4 pl-4 py-2 ${m.direction === "inbound" ? "border-[#0D9488] bg-[#0D9488]/5" : "border-[#D90429] bg-[#FFE5E9]/40"}`} data-testid={`msg-${m.id}`}>
                    <div className="flex items-center justify-between text-[10px] font-mono-shelv uppercase tracking-widest">
                      <span className={m.direction === "inbound" ? "text-[#0D9488]" : "text-[#D90429]"}>
                        {m.direction === "inbound" ? "← Inbound" : "→ Outbound"} · {m.status}
                      </span>
                      <span className="text-[#717680]">{(m.sent_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="font-semibold text-sm mt-1">{m.subject}</div>
                    <div className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: m.body_html || "" }}/>
                    {m.error && <div className="text-[10px] text-[#D90429] mt-1">Error: {m.error}</div>}
                  </div>
                ))}
              </div>
              <div className="border-t border-black/10 p-4 bg-[#FAFBFC]" data-testid="reply-form">
                <div className="overline text-[10px] mb-1 flex items-center gap-1"><Envelope size={12}/> Reply · threading preserved automatically</div>
                <input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} data-testid="reply-subject-input" placeholder="Subject" className="w-full border border-black/15 rounded-none px-3 py-2 mt-1 text-sm"/>
                <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} data-testid="reply-body-input" rows={5} placeholder="Type your reply — supports HTML" className="w-full border border-black/15 rounded-none px-3 py-2 mt-2 text-sm"/>
                <div className="flex items-center justify-end mt-2">
                  <button onClick={sendReply} disabled={replying} data-testid="send-reply-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40">
                    <PaperPlaneRight size={14} weight="bold"/> {replying ? "Sending…" : "Send Reply"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Layout>
  );
}
